import React from 'react';
import { Activity, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';

const InterventionEffectiveness = ({ interventions }) => {
  const getEffectivenessColor = (effectiveness) => {
    if (effectiveness >= 30) return 'text-green-600 bg-green-50';
    if (effectiveness >= 15) return 'text-yellow-600 bg-yellow-50';
    if (effectiveness >= 0) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  const getEffectivenessIcon = (effectiveness) => {
    if (effectiveness >= 15) return <TrendingUp className="w-6 h-6 text-green-500" />;
    if (effectiveness >= 0) return <Activity className="w-6 h-6 text-yellow-500" />;
    return <TrendingDown className="w-6 h-6 text-red-500" />;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-emerald-100 rounded-lg">
          <Activity className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Intervention Effectiveness</h2>
          <p className="text-sm text-gray-600">Measure the impact of your treatments and interventions</p>
        </div>
      </div>

      <div className="space-y-4">
        {interventions?.map((intervention, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-5">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                {getEffectivenessIcon(intervention?.effectiveness)}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg capitalize">
                      {intervention?.type} Intervention
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Based on {intervention?.sampleSize?.with + intervention?.sampleSize?.without} symptom entries
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getEffectivenessColor(intervention?.effectiveness)}`}>
                    {intervention?.effectiveness >= 0 ? '+' : ''}{intervention?.effectiveness?.toFixed(1)}% effective
                  </span>
                </div>

                {/* Comparison */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">With {intervention?.type}</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {intervention?.avgSeverityWith?.toFixed(1)}
                      <span className="text-sm text-gray-600 font-normal">/10</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {intervention?.sampleSize?.with} entries
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Without {intervention?.type}</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {intervention?.avgSeverityWithout?.toFixed(1)}
                      <span className="text-sm text-gray-600 font-normal">/10</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {intervention?.sampleSize?.without} entries
                    </div>
                  </div>
                </div>

                {/* Visual Comparison */}
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">With intervention</span>
                      <span className="font-medium text-gray-900">{intervention?.avgSeverityWith?.toFixed(1)}/10</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-green-500 h-3 rounded-full"
                        style={{ width: `${(intervention?.avgSeverityWith / 10) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Without intervention</span>
                      <span className="font-medium text-gray-900">{intervention?.avgSeverityWithout?.toFixed(1)}/10</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-red-500 h-3 rounded-full"
                        style={{ width: `${(intervention?.avgSeverityWithout / 10) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Confidence */}
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-gray-600">Statistical Confidence</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${intervention?.confidence * 100}%` }}
                      />
                    </div>
                    <span className="font-medium text-gray-900">{(intervention?.confidence * 100)?.toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {(!interventions || interventions?.length === 0) && (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No intervention data available</p>
            <p className="text-sm text-gray-500 mt-1">Track symptoms with and without interventions to see effectiveness</p>
          </div>
        )}
      </div>

      {interventions?.length > 0 && (
        <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-emerald-900">
              <strong>Key Finding:</strong> Your interventions show an average effectiveness of{' '}
              {(interventions?.reduce((sum, i) => sum + i?.effectiveness, 0) / interventions?.length)?.toFixed(1)}%
              in reducing symptom severity. Continue tracking to refine your treatment approach.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterventionEffectiveness;